import { RoadType, CutFillSection, RoadSummary, CarriagewayItems, CarriagewayTakeOff, CarriagewayFootway, Footways, FootwayConfiguration, Footpaths } from '../types/tender.types';

/**
 * Calculate total construction thickness for a road type (now includes capping)
 */
export const calculateTotalConstructionThickness = (roadType: Omit<RoadType, 'totalConstructionThickness'>): number => {
  return roadType.surfaceCourse.thickness +
         roadType.binderCourse.thickness +
         roadType.baseCourse.thickness +
         roadType.subBase.thickness +
         roadType.capping +
         (roadType.sandPave || 0);
};

/**
 * Calculate formation level based on finished road level and construction thickness
 */
export const calculateFormationLevel = (finishedRoadLevel: number, constructionThickness: number): number => {
  return finishedRoadLevel - (constructionThickness / 1000); // Convert mm to m
};

/**
 * Calculate stripped level after topsoil removal
 */
export const calculateStrippedLevel = (existingGroundLevel: number, topsoilStrip: number): number => {
  return existingGroundLevel - topsoilStrip;
};

/**
 * Calculate cut/fill depth using formation level - positive for cut, negative for fill
 */
export const calculateCutFillDepth = (existingGroundLevel: number, formationLevel: number): number => {
  return existingGroundLevel - formationLevel;
};

/**
 * Calculate cut and fill volumes for a section
 */
export const calculateCutFill = (
  area: number, 
  cutFillDepth: number
): { cutVolume: number; fillVolume: number } => {
  if (cutFillDepth > 0) {
    // Cut required (excess material)
    return {
      cutVolume: area * cutFillDepth,
      fillVolume: 0
    };
  } else {
    // Fill required (import material)
    return {
      cutVolume: 0,
      fillVolume: area * Math.abs(cutFillDepth)
    };
  }
};

/**
 * Update all calculated fields for a cut/fill section
 */
export const updateCutFillSection = (
  section: Omit<CutFillSection, 'formationLevel' | 'depth' | 'cutVolume' | 'fillVolume'>,
  roadConstructionThickness: number
): CutFillSection => {
  const formationLevel = calculateFormationLevel(section.finishedRoadLevel, roadConstructionThickness);
  const depth = calculateCutFillDepth(section.existingGroundLevel, formationLevel);
  const { cutVolume, fillVolume } = calculateNewCutFill(section.roadArea || 0, depth);

  return {
    ...section,
    roadConstructionThickness,
    formationLevel,
    depth,
    cutVolume,
    fillVolume
  };
};

/**
 * Calculate road summary totals
 */
export const calculateRoadSummary = (
  cutFillSections: CutFillSection[],
  roadTypes: RoadType[]
): RoadSummary => {
  const totalCutVolume = cutFillSections.reduce((sum, section) => sum + section.cutVolume, 0);
  const totalFillVolume = cutFillSections.reduce((sum, section) => sum + section.fillVolume, 0);
  const netBalance = totalCutVolume - totalFillVolume; // Positive = excess cut, Negative = need fill

  // Calculate total road area by road type
  const totalAreaByType: { [roadTypeId: string]: number } = {};
  roadTypes.forEach(roadType => {
    totalAreaByType[roadType.id] = cutFillSections
      .filter(section => section.roadTypeId === roadType.id)
      .reduce((sum, section) => sum + (section.roadArea || 0), 0);
  });

  return {
    totalCutVolume,
    totalFillVolume,
    netBalance,
    totalAreaByType
  };
};

/**
 * Check if earthworks volume is significant (>1000m³)
 */
export const isSignificantEarthworks = (cutVolume: number, fillVolume: number): boolean => {
  return (cutVolume + fillVolume) > 1000;
};

/**
 * Calculate total carriageway quantities
 */
export const calculateCarriagewayTotals = (carriageway: CarriagewayItems) => {
  const totalKerbs = carriageway.kerbs.precastConcrete + 
                   carriageway.kerbs.naturalStone + 
                   carriageway.kerbs.conservation;
  
  const totalFootpaths = carriageway.footpaths.concrete + 
                        carriageway.footpaths.tarmac + 
                        carriageway.footpaths.block;
  
  const totalCrossovers = carriageway.crossovers.standard + carriageway.crossovers.heavy;
  
  const totalRoadMarkings = carriageway.roadMarkings.whiteLine + 
                           carriageway.roadMarkings.yellowLine + 
                           carriageway.roadMarkings.hatching;

  return {
    totalKerbs,
    totalFootpaths,
    totalCrossovers,
    totalRoadMarkings
  };
};

/**
 * Validate road type configuration
 */
export const validateRoadType = (roadType: Partial<RoadType>): string[] => {
  const errors: string[] = [];

  if (!roadType.name || roadType.name.trim() === '') {
    errors.push('Road type name is required');
  }

  if (!roadType.surfaceCourse?.thickness || roadType.surfaceCourse.thickness <= 0) {
    errors.push('Surface course thickness must be greater than 0');
  }

  if (!roadType.binderCourse?.thickness || roadType.binderCourse.thickness <= 0) {
    errors.push('Binder course thickness must be greater than 0');
  }

  if (!roadType.baseCourse?.thickness || roadType.baseCourse.thickness <= 0) {
    errors.push('Base course thickness must be greater than 0');
  }

  if (!roadType.subBase?.thickness || roadType.subBase.thickness <= 0) {
    errors.push('Sub-base thickness must be greater than 0');
  }

  return errors;
};

/**
 * Validate cut/fill section
 */
export const validateCutFillSection = (section: Partial<CutFillSection>): string[] => {
  const errors: string[] = [];

  if (!section.chainage || section.chainage.trim() === '') {
    errors.push('Chainage is required');
  }

  if (!section.roadTypeId || section.roadTypeId.trim() === '') {
    errors.push('Road type must be selected');
  }


  if (section.finishedRoadLevel === undefined || section.finishedRoadLevel === null) {
    errors.push('Finished road level is required');
  }

  if (section.existingGroundLevel === undefined || section.existingGroundLevel === null) {
    errors.push('Existing ground level is required');
  }


  return errors;
};

// New calculation functions for missing functionality

/**
 * Calculate carriageway takeoff total
 */
export const calculateCarriagewayTotal = (takeOff: Omit<CarriagewayTakeOff, 'total'>): number => {
  return takeOff.tarmacRoad + takeOff.blockPaved;
};

/**
 * Calculate footways total path
 */
export const calculateFootwaysTotal = (footways: Omit<Footways, 'totalPath'>): number => {
  return footways.tarmacPath + footways.blockPavedPathXover + footways.crossover + footways.verge;
};

/**
 * Calculate footway configuration total
 */
export const calculateFootwayConfigTotal = (config: Omit<FootwayConfiguration, 'total'>): number => {
  return config.surface + config.binder + config.base + config.type1;
};

/**
 * Calculate road area from carriageway takeoff data
 */
export const calculateRoadArea = (takeOff: CarriagewayTakeOff, numberOfChainages: number): number => {
  return takeOff.total / numberOfChainages;
};

/**
 * One road's Cut & Fill/Footpaths chainage rows, grouped for per-road display/totals.
 * Shared shape used by both `RoadsTakeoff.tsx` (Cut & Fill/Footpaths/Capping Layers tabs)
 * and `RoadsBillSummary.tsx` (Carriageway Summary), so both compute the same live values.
 */
export interface CutFillRoadGroup {
  roadId: string;
  roadName: string;
  sections: CutFillSection[];
}

/** Groups raw Cut & Fill sections by road, seeding an empty group for every road that has none yet. */
export const groupCutFillSectionsByRoad = (
  cutFillSections: CutFillSection[],
  carriagewayFootways: CarriagewayFootway[]
): CutFillRoadGroup[] => {
  const map = new Map<string, CutFillRoadGroup>();
  for (const cf of carriagewayFootways) {
    map.set(cf.id, { roadId: cf.id, roadName: cf.roadName, sections: [] });
  }
  for (const s of cutFillSections) {
    if (!map.has(s.carriagewayFootwayId)) {
      const displayName = s.carriagewayFootwayId.replace(/^imported_cf_/, '').replace(/_/g, ' ');
      map.set(s.carriagewayFootwayId, { roadId: s.carriagewayFootwayId, roadName: displayName, sections: [] });
    }
    map.get(s.carriagewayFootwayId)!.sections.push(s);
  }
  return Array.from(map.values());
};

// Carriageway Cut & Fill formulas (per R&S-Takeoffsheet.xlsx, "Carriageway Take off" sheet, row 24: AY:BG):
// Formation Level = FRL - Road Construction; Less Strip = Extg Ground - Strip Value;
// Depth = Less Strip - Formation Level; Road Area = Total Road Area / No. of Chainages;
// Capping Area = 0 if Road Area = 0, else Road Area + (10 * Kerb Race Width * 2)
// (Kerb Race Width configurable via Kerbs & Edgings tab's Configure modal, default 0.5m);
// Cut Vol = 0 if Road Area = 0, else (Depth if Depth > 0 else 0) * Capping Area
//           + (0.5 * Depth^2 * (Next Chainage - Current Chainage) * 2) when Depth >= 0.5
//           — the second term is the extra triangular volume for the slope transition
//           between this chainage and the next one (Excel: SUMIF(BC,">0")*BD + IF(BC>=0.5,(0.5*BC*BC*(AV_next-AV))*2,0)).
// Fill Vol mirrors Cut Vol on the negative side (Excel's Fill Vol row applies the identical
// SUMIF/IF(<=-0.5) test, but against that row's own Depth sign, which is negative for fill),
// using the same immediate next chainage as Cut Vol:
// Fill Vol = 0 if Road Area = 0, else (|Depth| if Depth < 0 else 0) * Capping Area
//           + (0.5 * Depth^2 * (Next Chainage - Current Chainage) * 2) when Depth <= -0.5.
export const computeCutFillFields = (
  section: CutFillSection,
  roadType: RoadType | undefined,
  carriagewayFootway: CarriagewayFootway | undefined,
  numberOfChainages: number,
  stripValueDefault: number,
  kerbRaceWidth: number,
  nextChainageDelta: number
): Pick<
  CutFillSection,
  | "roadConstructionThickness"
  | "formationLevel"
  | "lessStrip"
  | "depth"
  | "roadArea"
  | "cappingArea"
  | "cutVolume"
  | "fillVolume"
> => {
  const roadConstructionThickness = roadType?.totalConstructionThickness || 0;
  const formationLevel = (section.frl || 0) - roadConstructionThickness;
  const lessStrip = (section.existingGroundLevel || 0) - stripValueDefault;
  const depth = lessStrip - formationLevel;

  const totalRoadArea = carriagewayFootway?.totalRoadArea || 0;
  const roadArea = numberOfChainages > 0 ? totalRoadArea / numberOfChainages : 0;
  const cappingArea = roadArea === 0 ? 0 : roadArea + 10 * kerbRaceWidth * 2;

  let cutVolume = 0;
  let fillVolume = 0;
  if (roadArea !== 0) {
    const cutDepthTerm = depth > 0 ? depth : 0;
    const cutSlopeTerm = depth >= 0.5 ? 0.5 * depth * depth * nextChainageDelta * 2 : 0;
    cutVolume = cutDepthTerm * cappingArea + cutSlopeTerm;

    const fillDepthTerm = depth < 0 ? Math.abs(depth) : 0;
    const fillSlopeTerm = depth <= -0.5 ? 0.5 * depth * depth * nextChainageDelta * 2 : 0;
    fillVolume = fillDepthTerm * cappingArea + fillSlopeTerm;
  }

  return {
    roadConstructionThickness,
    formationLevel,
    lessStrip,
    depth,
    roadArea,
    cappingArea,
    cutVolume,
    fillVolume,
  };
};

// Footpaths formulas (per R&S-Takeoffsheet.xlsx, "Carriageway Take off" sheet, row 24: BI:BO):
// Footpath Level = FRL + 0.125; Path Formation Level = Footpath Level - Average Total build-up;
// Less Strip = Existing Ground - Strip value; Depth = Less Strip - Path Formation Level;
// Cut Vol = Path Area * Depth (Depth > 0); Fill Vol = Path Area * |Depth| (Depth < 0);
// Path Area = Total Path / No. of Chainages.
export const computeFootpathsFields = (
  section: CutFillSection,
  carriagewayFootway: CarriagewayFootway | undefined,
  numberOfChainages: number,
  stripValueDefault: number,
  footpathsAverageBuildUp: number
): Pick<
  CutFillSection,
  | "fpl"
  | "pathFormLevel"
  | "footpathsLessStrip"
  | "footpathsDepth"
  | "footpathsCutVolume"
  | "footpathsFillVolume"
  | "pathArea"
> => {
  const fpl = (section.frl || 0) + 0.125;
  const pathFormLevel = fpl - footpathsAverageBuildUp;
  const footpathsLessStrip = (section.existingGroundLevel || 0) - stripValueDefault;
  const footpathsDepth = footpathsLessStrip - pathFormLevel;

  const totalPath = carriagewayFootway?.totalPath || 0;
  const pathArea = numberOfChainages > 0 ? totalPath / numberOfChainages : 0;

  let footpathsCutVolume = 0;
  let footpathsFillVolume = 0;
  if (footpathsDepth > 0) {
    footpathsCutVolume = pathArea * footpathsDepth;
  } else if (footpathsDepth < 0) {
    footpathsFillVolume = pathArea * Math.abs(footpathsDepth);
  }

  return {
    fpl,
    pathFormLevel,
    footpathsLessStrip,
    footpathsDepth,
    footpathsCutVolume,
    footpathsFillVolume,
    pathArea,
  };
};

/** Live Cut & Fill values (Road Area/Capping Area/Cut Vol/Fill Vol) per road group — see `computeCutFillFields`. */
export const computeLiveCutFillGroups = (
  groups: CutFillRoadGroup[],
  carriagewayFootways: CarriagewayFootway[],
  roadTypes: RoadType[],
  stripValueDefault: number,
  kerbRaceWidth: number
): CutFillRoadGroup[] => {
  return groups.map((group) => {
    const carriagewayFootway = carriagewayFootways.find((cf) => cf.id === group.roadId);
    // No. of Chainages = count of DISTINCT chainage stations for this road,
    // matching Excel's COUNTA(AV24:AV124) over that road's chainage block.
    // Using group.sections.length directly double-counted whenever a road had
    // more than one row sharing the same chainage value (e.g. a duplicate row
    // from re-importing/re-adding a station) - that inflated the denominator
    // here and silently shrank Road Area/Capping Area/Cut Vol/Fill Vol for
    // every row on that road.
    const numberOfChainages = new Set(group.sections.map((s) => s.chainage)).size;
    // Chainage deltas — sections are matched by ascending chainage value (not array
    // order) to find each row's immediate neighbour below it in the Excel sheet.
    // Both Cut Vol's and Fill Vol's slope terms use the immediate next chainage
    // (Excel: AV25-AV24, 1 row down). No such neighbour for the last chainage on a
    // road, so its delta is 0 there.
    const sortedChainages = group.sections
      .map((s) => parseInt(s.chainage))
      .filter((n) => !isNaN(n))
      .sort((a, b) => a - b);
    const nextChainageDeltaById = new Map<string, number>();
    for (const section of group.sections) {
      const current = parseInt(section.chainage);
      const currentIndex = sortedChainages.indexOf(current);
      const next = sortedChainages[currentIndex + 1];
      nextChainageDeltaById.set(section.id, next !== undefined ? next - current : 0);
    }
    return {
      ...group,
      sections: group.sections.map((section) => {
        const roadType = roadTypes.find((rt) => rt.id === section.roadTypeId);
        return {
          ...section,
          ...computeCutFillFields(
            section,
            roadType,
            carriagewayFootway,
            numberOfChainages,
            stripValueDefault,
            kerbRaceWidth,
            nextChainageDeltaById.get(section.id) || 0
          ),
        };
      }),
    };
  });
};

/**
 * Collapse duplicate-chainage rows to one representative row per distinct chainage.
 * `computeLiveCutFillGroups`/`computeLiveFootpathsGroups` already divide roadArea/
 * cappingArea/cutVolume/fillVolume by the distinct chainage count (not raw row
 * count) so each row holds the correct per-chainage share — but summing those
 * per-row values back across the raw (still duplicate-containing) `sections`
 * array double-counts any chainage with more than one row. Use this before any
 * such sum (Cut/Fill/Capping totals in accordion headers, grand totals, Capping
 * Layers tab quantities).
 */
export const dedupeSectionsByChainage = <T extends { chainage?: string }>(sections: T[]): T[] => {
  const seen = new Set<string | undefined>();
  return sections.filter((s) => {
    if (seen.has(s.chainage)) return false;
    seen.add(s.chainage);
    return true;
  });
};

/** Live Footpaths values (FPL/Path Formation Level/Cut Vol/Fill Vol/Path Area) per road group — see `computeFootpathsFields`. */
export const computeLiveFootpathsGroups = (
  groups: CutFillRoadGroup[],
  carriagewayFootways: CarriagewayFootway[],
  stripValueDefault: number,
  footpathsAverageBuildUp: number
): CutFillRoadGroup[] => {
  return groups.map((group) => {
    const carriagewayFootway = carriagewayFootways.find((cf) => cf.id === group.roadId);
    // See computeLiveCutFillGroups above - counts distinct chainage stations,
    // not raw row count, so a duplicate-chainage row doesn't inflate this.
    const numberOfChainages = new Set(group.sections.map((s) => s.chainage)).size;
    return {
      ...group,
      sections: group.sections.map((section) => ({
        ...section,
        ...computeFootpathsFields(
          section,
          carriagewayFootway,
          numberOfChainages,
          stripValueDefault,
          footpathsAverageBuildUp
        ),
      })),
    };
  });
};

/**
 * Calculate less strip value
 */
export const calculateLessStrip = (existingGroundLevel: number, stripValue: number): number => {
  return existingGroundLevel - stripValue;
};

/**
 * Calculate depth for new methodology
 */
export const calculateDepth = (formationLevel: number, lessStrip: number): number => {
  return formationLevel - lessStrip;
};

/**
 * Calculate FPL (Footpath Level) based on road type
 */
export const calculateFPL = (frl: number, roadType: 'tarmac' | 'block'): number => {
  if (roadType === 'tarmac') {
    return frl + 0.125;
  } else { // block paved
    return frl + 0.05;
  }
};

/**
 * Calculate Path Area
 */
export const calculatePathArea = (totalPath: number, numberOfChainages: number): number => {
  return totalPath / numberOfChainages;
};

/**
 * Calculate Path Form Level (complex calculation)
 */
export const calculatePathFormLevel = (
  fpl: number,
  roads: Array<{tarmacPath: number, blockPavedPathXover: number, crossover: number, verge: number}>,
  footwayConfigs: FootwayConfiguration[],
  totalPath: number
): number => {
  const tarmacPathTotal = roads.reduce((sum, road) => sum + road.tarmacPath, 0);
  const blockPavedTotal = roads.reduce((sum, road) => sum + road.blockPavedPathXover, 0);
  const crossoverTotal = roads.reduce((sum, road) => sum + road.crossover, 0);
  const vergeTotal = roads.reduce((sum, road) => sum + road.verge, 0);
  
  const tarmacConfig = footwayConfigs.find(config => config.name.toLowerCase().includes('tarmac'));
  const blockConfig = footwayConfigs.find(config => config.name.toLowerCase().includes('block'));
  const crossoverConfig = footwayConfigs.find(config => config.name.toLowerCase().includes('crossover'));
  const vergeConfig = footwayConfigs.find(config => config.name.toLowerCase().includes('verge'));
  
  const calculation = (
    (tarmacPathTotal * (tarmacConfig?.total || 0)) +
    (blockPavedTotal * (blockConfig?.total || 0)) +
    (crossoverTotal * (crossoverConfig?.total || 0)) +
    (vergeTotal * (vergeConfig?.total || 0))
  ) / totalPath;
  
  return fpl - calculation;
};

/**
 * Calculate cut and fill volumes for new methodology
 */
export const calculateNewCutFill = (
  area: number, 
  depth: number
): { cutVolume: number; fillVolume: number } => {
  if (depth > 0) {
    // Cut required
    return {
      cutVolume: area * depth,
      fillVolume: 0
    };
  } else if (depth < 0) {
    // Fill required
    return {
      cutVolume: 0,
      fillVolume: area * Math.abs(depth)
    };
  } else {
    // No cut or fill
    return {
      cutVolume: 0,
      fillVolume: 0
    };
  }
};